import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { useToggle } from "../../../hooks/state";
import {
  PostCancelAppointmentVariables,
  postCancelAppointment,
} from "../../../apis/appointmentApi";
import { AppContext } from "../../../context/app";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import AppointmentCancelModal from "./AppointmentCancelModal";

type CancelPayload = PostCancelAppointmentVariables["payload"];

interface ContextValue {
  showCancelModal: (appointmentUuid: string, options: Options) => void;
  cancelAppointment: (
    appointmentUuid: string,
    payload: CancelPayload,
    options: Options,
  ) => Promise<void>;
}

interface AppointmentCancelProviderProps {
  children?: ReactNode;
}

interface Options {
  onSuccess?: VoidFunction;
  onError?: (error: unknown) => void;
}

const Context = createContext<ContextValue | undefined>(undefined);

function AppointmentCancelProvider(props: AppointmentCancelProviderProps) {
  const { children } = props;

  const { client } = useContext(AppContext);

  const [modalVisible, modalActions] = useToggle(false);

  const [appointmentUuid, setAppointmentUuid] = useState<string>();

  const [options, setOptions] = useState<Options>({});

  const postCancelAppointmentMutation = useMutation(postCancelAppointment);

  const cancelAppointment = useCallback(
    async (uuid: string, payload: CancelPayload, options: Options) => {
      try {
        await postCancelAppointmentMutation.mutateAsync({
          clientUuid: client!.uuid,
          appointmentUuid: uuid,
          payload,
        });
        toast.success("Appointment Cancelled", defaultToastOptions);
        options?.onSuccess?.();
      } catch (error) {
        toast.error(extractError(error), defaultToastOptions);
        options?.onError?.(error);
      }
    },
    [client, postCancelAppointmentMutation],
  );

  const handleSuccess = useCallback(() => {
    options.onSuccess?.();
    modalActions.off();
  }, [options, modalActions]);

  const showCancelModal = useCallback(
    (uuid: string, options: Options = {}) => {
      setAppointmentUuid(uuid);
      setOptions(options);
      modalActions.on();
    },
    [modalActions],
  );

  const contextValue = useMemo(() => {
    return { showCancelModal, cancelAppointment };
  }, [showCancelModal, cancelAppointment]);

  return (
    <Context.Provider value={contextValue}>
      {children}
      <AppointmentCancelModal
        visible={modalVisible}
        appointmentUuid={appointmentUuid!}
        onClose={modalActions.off}
        onSuccess={handleSuccess}
      />
    </Context.Provider>
  );
}

export function useAppointmentCancel() {
  const contextValue = useContext(Context);
  if (!contextValue) {
    throw new Error(
      "useAppointmentCancel must to be used inside an AppointmentCancelProvider",
    );
  }
  return contextValue;
}

export default AppointmentCancelProvider;
